import React, { useEffect, useState } from "react";
import {
	flexRender,
	getCoreRowModel,
	getFilteredRowModel,
	useReactTable,
} from "@tanstack/react-table";

import { Card, CardBody, Table, Col, Row } from "reactstrap";
import dayjs from "dayjs";
import "dayjs/locale/nl";
import weekOfYear from "dayjs/plugin/weekOfYear";

import SelectCell from "./SelectCell";
import ShiftStatusCell from "./ShiftStatusCell";
import AvailabilityShiftCell from "./AvailabilityShiftCell";
import FilterHeaderCell from "./FilterHeaderCell";
import ControlTable from "./ControlTable";
import { useControlshermContext } from "../context/ControlshermContext";

dayjs.locale("nl");
dayjs.extend(weekOfYear);

const columns = [
	{
		header: "Dag",
		columns: [
			{
				accessorKey: "day",
				header: () => <div className="text-start">Datum</div>,
				cell: (info) => {
					const val = info.getValue();
					const date = dayjs(val, "DD-MM-YYYY");
					return <>{date.format("dd DD-MM-YYYY") ?? "--"}</>;
				},
			},
			{
				accessorKey: "week",
				header: "Week",
				cell: (props) => {
					const val = props.getValue();
					return <div>{val ?? "--"}</div>;
				},
			},
		],
	},
	{
		header: "Ochtend",
		columns: [
			{
				id: "ochtendSelected",
				header: "",
				accessorFn: (row) => row.data?.Ochtend,
				cell: SelectCell,
			},
			{
				id: "ochtendStatus",
				header: "Status",
				accessorFn: (row) => row.data?.Ochtend?.status,
				cell: ShiftStatusCell,
			},
			{
				id: "ochtendAvailability",
				header: "Beschikbaarheid",
				accessorFn: (row) => ({
					availability: row.data?.Ochtend?.availability,
					availabilityError: row.data?.Ochtend?.availabilityError,
					substitutor: row.data?.Ochtend?.substitutor,
					isVisible: row.data?.Ochtend?.isVisible,
				}),
				cell: AvailabilityShiftCell,
			},
		],
	},
	{
		header: "Middag",
		columns: [
			{
				id: "middagSelected",
				header: "",
				accessorFn: (row) => row.data?.Middag,
				cell: SelectCell,
			},
			{
				id: "middagStatus",
				header: "Status",
				accessorFn: (row) => row.data?.Middag?.status,
				cell: ShiftStatusCell,
			},
			{
				id: "middagAvailability",
				header: "Beschikbaarheid",
				accessorFn: (row) => ({
					availability: row.data?.Middag?.availability,
					availabilityError: row.data?.Middag?.availabilityError,
					substitutor: row.data?.Middag?.substitutor,
					isVisible: row.data?.Middag?.isVisible,
				}),
				cell: AvailabilityShiftCell,
			},
		],
	},
];

export default function DatesTable() {
	const { dates: data, setDates: setData, selectedDates, refetchDates } = useControlshermContext();
	const [isControlVisible, setIsControlVisible] = useState(false);
	const [filteredData, setFilteredData] = useState([]);
	const [hideRequested, setHideRequested] = useState(true);
	const [hideUnselected, setHideUnselected] = useState(false);
	const [availabilityFilter, setAvailabilityFilter] = useState([]);
	const [columnFilters, setColumnFilters] = useState([]);

	const updateIsSelected = (rowIndex, shiftType, newValue) => {
		const newData = data.map((row, i) => {
			if (i !== rowIndex) return row;
			return {
				...row,
				data: {
					...row.data,
					[shiftType]: {
						...row.data[shiftType],
						isSelected: newValue,
					},
				},
			};
		});
		setData(newData);
	};
	useEffect(() => {
		refetchDates();
	}, []);

	const table = useReactTable({
		data: filteredData,
		columns,
		getCoreRowModel: getCoreRowModel(),
		getFilteredRowModel: getFilteredRowModel(),
		state: {
			columnFilters,
		},
		meta: {
			dates: data,
			updateIsSelected: updateIsSelected,
		},
	});

	function filterDataByDaypart(rows) {
		if (!Array.isArray(rows)) return [];
		return rows
			.map((row) => {
				const filteredShifts = {};
				const dayparts = ["Ochtend", "Middag"];

				for (const part of dayparts) {
					const shift = row.data?.[part];
					if (!shift) continue;

					const passesRequested = !hideRequested || shift.status === "Openstaand";
					const passesUnselected = !hideUnselected || shift.isSelected === true;
					const passesAvailability =
						availabilityFilter.length === 0 ||
						availabilityFilter.includes(shift.availability);

					if (passesRequested && passesUnselected && passesAvailability) {
						filteredShifts[part] = shift;
					}
				}
				if (Object.keys(filteredShifts).length > 0) {
					return {
						...row,
						data: filteredShifts,
					};
				}
				return null;
			})
			.filter(Boolean);
	}

	useEffect(() => {
		const filtered = filterDataByDaypart(data);
		setFilteredData(filtered);
	}, [data, hideRequested, hideUnselected, availabilityFilter]);

	return (
		<Row className="row-cols-1">
			{isControlVisible && (
				<Col>
					<ControlTable
						updateIsSelected={updateIsSelected}
						hideRequested={hideRequested}
						hideUnselected={hideUnselected}
						setHideRequested={setHideRequested}
						setHideUnselected={setHideUnselected}
						availabilityFilter={availabilityFilter}
						setAvailabilityFilter={setAvailabilityFilter}
					/>
				</Col>
			)}
			<Col>
				<Card className="rounded-4 main-card">
					<CardBody className="p-3">
						<div className="table-wrapper">
						<Table
							size="sm"
							className="mb-0 controlsherm__table controlsherm__table--avail controlshermm__table table-hover"
						>
							<thead>
								{table.getHeaderGroups().map((headerGroup) => {
									return (
										<tr key={headerGroup.id}>
											{headerGroup.headers.map((header) => {
												const groupsArray = header?.column?.parent?.columns;
												const headerItem = groupsArray?.find(
													(i) => i.id === header.id
												);
												const index = groupsArray?.indexOf(headerItem);
												const isLastInGroup =
													index + 1 === groupsArray?.length ||
													groupsArray === undefined;
												const columnName = header.column.columnDef.header;

												return (
													<th
														key={header.id}
														colSpan={header.colSpan}
														className={
															isLastInGroup ? "border-end" : ""
														}
													>
														{columnName === "Dag" ? (
															<FilterHeaderCell
																handleClick={() =>
																	setIsControlVisible(
																		(prev) => !prev
																	)
																}
																allRows={data}
																visibleRows={selectedDates}
															/>
														) : (
															flexRender(
																header.column.columnDef.header,
																header.getContext()
															)
														)}
													</th>
												);
											})}
										</tr>
									);
								})}
							</thead>
							<tbody>
								{table.getRowModel().rows.map((row) => {
									return (
										<tr key={row.id}>										
											{row.getVisibleCells().map((cell) => {
												const groupColumns = cell.column.parent?.columns ?? [];

												const isLastInGroup =
													groupColumns[groupColumns.length - 1] === cell.column;

												const shiftType = cell.column.parent?.columnDef?.header;

												const isShiftDisabled =
													shiftType === "Ochtend"
														? row.original?.data?.Ochtend?.isVisible === false
														: shiftType === "Middag"
															? row.original?.data?.Middag?.isVisible === false
															: false;

												return (
													<td
														key={cell.id}
														className={`${isLastInGroup ? "border-end" : ""} ${isShiftDisabled ? "controlsherm__shift-disabled" : ""
															}`}
													>
														{flexRender(
															cell.column.columnDef.cell,
															cell.getContext()
														)}
													</td>
												);
											})}
										</tr>
									);
								})}
							</tbody>
						</Table>
						</div>
					</CardBody>
				</Card>
			</Col>
		</Row>
	);
}
